import React, { useState } from 'react';
import { hrmsCreateInterview } from '../../lib/hrmsApi';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import { Loader2, CalendarPlus } from 'lucide-react';

/**
 * Modal used from the Pipeline to schedule an interview for a specific application.
 * Props: application ({id, full_name, email, job_title}), open, onClose, onCreated
 */
const InterviewScheduleModal = ({ application, open, onClose, onCreated }) => {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    round_name: 'Round 1 · Technical',
    date: '',
    time_start: '11:00',
    time_end: '12:00',
    timezone: 'IST',
    mode: 'Video',
    location: '',
    interviewers: '',
    notes: '',
  });

  const submit = async () => {
    if (!form.date || !form.time_start) {
      toast({ title: 'Please set a date and start time' });
      return;
    }
    setSaving(true);
    try {
      const body = {
        application_id: application.id,
        round_name: form.round_name,
        date: form.date,
        time_start: form.time_start,
        time_end: form.time_end,
        timezone: form.timezone,
        mode: form.mode,
        location: form.location,
        notes: form.notes,
        interviewers: form.interviewers.split(',').map((s) => s.trim()).filter(Boolean),
      };
      const created = await hrmsCreateInterview(body);
      toast({ title: 'Interview scheduled', description: `Invite emailed to ${application?.email}` });
      onCreated && onCreated(created);
      onClose && onClose();
    } catch (err) {
      toast({ title: 'Schedule failed', description: err?.response?.data?.detail || '' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose && onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogTitle>Schedule interview · {application?.full_name}</DialogTitle>
        <div className="text-xs text-slate-500 -mt-1 mb-2">{application?.job_title} · {application?.email}</div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Round name</Label><Input value={form.round_name} onChange={(e) => setForm({ ...form, round_name: e.target.value })} placeholder="Round 1 · Technical / HR / Final" data-testid="schedule-round" /></div>
          <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="schedule-date" /></div>
          <div><Label>Timezone</Label>
            <select value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} className="mt-1 h-10 w-full rounded-md border border-slate-200 px-3 text-sm">
              <option>IST</option><option>GMT</option><option>CET</option><option>EST</option><option>PST</option><option>SGT</option>
            </select>
          </div>
          <div><Label>Start time</Label><Input type="time" value={form.time_start} onChange={(e) => setForm({ ...form, time_start: e.target.value })} data-testid="schedule-time-start" /></div>
          <div><Label>End time</Label><Input type="time" value={form.time_end} onChange={(e) => setForm({ ...form, time_end: e.target.value })} data-testid="schedule-time-end" /></div>
          <div><Label>Mode</Label>
            <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="mt-1 h-10 w-full rounded-md border border-slate-200 px-3 text-sm" data-testid="schedule-mode">
              <option>Video</option><option>Phone</option><option>In-person</option>
            </select>
          </div>
          <div><Label>Location / Meeting link</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Zoom link / Room 3" /></div>
          <div className="col-span-2"><Label>Interviewers (comma-separated emails)</Label><Input value={form.interviewers} onChange={(e) => setForm({ ...form, interviewers: e.target.value })} placeholder="lead@hipro.com, cto@hipro.com" data-testid="schedule-interviewers" /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Focus areas, JD link…" /></div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="schedule-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CalendarPlus className="h-4 w-4 mr-1" /> Schedule &amp; email invite</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InterviewScheduleModal;
